import React, { useEffect, useRef, useState } from "react";
import * as stock from "../../../services/inventoryStockService";
import { AppDropdown, inputStyle, useLoad } from "../../../components/ui";

// Shared pieces of the inventory upgrade screens: the vendor's switches and what the user may do,
// warehouse / supplier / item pickers, and the batch / serial input for tracked items.

// { multi_warehouse, purchasing_enabled, ..., can: { manage, approve, purchase, count } }
export function useInvSettings() {
  return useLoad(() => stock.getUpgradeSettings(), []);
}

export const fmtQty = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
};
export const fmtAmt = (n) => (n === null || n === undefined || n === "" ? "" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export const TYPE_LABELS = { STOCK: "Stocked", CONSUMABLE: "Consumable", NON_STOCK: "Non-stock", KIT: "Kit" };
export const TRACKING_LABELS = { NONE: "Not tracked", BATCH: "Batch + expiry", SERIAL: "Serial numbers" };
export const WAREHOUSE_TYPES = [
  { value: "MAIN", label: "Main store" },
  { value: "SUBINV", label: "Sub-store / workshop stores" },
  { value: "YARD", label: "Vehicle yard" },
  { value: "DEMO", label: "Demo vehicles" },
  { value: "USED", label: "Used vehicles" },
  { value: "IN_TRANSIT", label: "In transit" },
];

export function WarehouseSelect({ id, value, onChange, all = false, placeholder = "Warehouse", disabled = false }) {
  const { data } = useLoad(() => stock.listWarehouses({ status: "ACTIVE" }), []);
  const opts = (data || []).map((w) => ({ label: `${w.code} – ${w.name}${w.is_default ? " (default)" : ""}`, value: w.id }));
  return <AppDropdown id={id} value={value} options={all ? [{ label: "All warehouses", value: null }, ...opts] : opts} onChange={(e) => onChange(e.value)} placeholder={placeholder} disabled={disabled} />;
}

export function SupplierSelect({ id, value, onChange, disabled = false }) {
  const { data } = useLoad(() => stock.listSuppliers({ status: "ACTIVE" }), []);
  return <AppDropdown id={id} value={value} filter options={(data || []).map((s) => ({ label: `${s.name} (${s.code})`, value: s.id }))} onChange={(e) => onChange(e.value)} placeholder="Supplier" disabled={disabled} />;
}

// An item picker that searches the server as you type. onChange(item) gets the whole row
// ({ id, sku, name, unit_of_measure, purchase_uom, purchase_uom_factor, tracking, item_type, warehouse_qty ... }).
export function ItemPicker({ id, value, onChange, warehouseId = null, itemType = null, placeholder = "Item (SKU or name)" }) {
  const [rows, setRows] = useState([]);
  const timer = useRef(null);
  const load = (q) =>
    stock
      .searchItems({ q, warehouse_id: warehouseId, item_type: itemType, size: 40 })
      .then((res) => setRows(res?.data || []))
      .catch(() => setRows([]));
  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, itemType]);
  const opts = rows.map((r) => ({
    label: `${r.sku} – ${r.name}`,
    value: r.id,
    description: `${r.unit_of_measure || ""}${r.tracking && r.tracking !== "NONE" ? ` · ${TRACKING_LABELS[r.tracking]}` : ""}${warehouseId ? ` · ${fmtQty(r.warehouse_qty)} here` : ""}`,
  }));
  return (
    <AppDropdown
      id={id}
      value={value}
      filter
      options={opts}
      placeholder={placeholder}
      onFilter={(e) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => load(e.filter), 300);
      }}
      onChange={(e) => onChange(rows.find((r) => r.id === e.value) || null)}
    />
  );
}

// Batches (lot + expiry or DOT week / year) or serial numbers for `qty` units of a tracked item.
//   mode "in": what arrives (new batches / serials); mode "out": choose from what is in stock (batches optional:
//   left empty, the earliest expiry goes first).
export function TrackingInput({ item, qty, value, onChange, mode = "in", itemId = null, warehouseId = null }) {
  const tracking = item ? item.tracking : "NONE";
  const { data: onHand } = useLoad(() => (mode === "out" && itemId && tracking !== "NONE" ? stock.getItemTracking(itemId, warehouseId) : Promise.resolve({ data: null })), [itemId, warehouseId, tracking, mode]);
  if (!item || tracking === "NONE") return null;
  const list = Array.isArray(value) ? value : [];
  if (tracking === "SERIAL") {
    const text = list.map((d) => d.serial_no).join("\n");
    return (
      <div style={{ marginTop: 6 }}>
        <textarea
          className="form-control"
          rows={Math.min(6, Math.max(2, Number(qty) || 1))}
          placeholder={mode === "in" ? `Serial numbers, one per line (${Number(qty) || 0})` : `Serial numbers going out, one per line (${Number(qty) || 0})`}
          aria-label="Serial numbers"
          value={text}
          onChange={(e) => onChange(e.target.value.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).map((s) => ({ serial_no: s })))}
        />
        {mode === "out" && onHand && onHand.serials ? <div style={{ fontSize: 12, color: "#6B7280" }}>In stock: {onHand.serials.map((s) => s.serial_no).join(", ") || "none"}</div> : null}
      </div>
    );
  }
  const set = (i, patch) => onChange(list.map((d, k) => (k === i ? { ...d, ...patch } : d)));
  const total = list.reduce((s, d) => s + Number(d.qty || 0), 0);
  return (
    <div style={{ marginTop: 6 }}>
      {list.map((d, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 mb-1">
          {mode === "out" && onHand ? (
            <select className="form-select form-select-sm" style={{ width: 200 }} aria-label="Batch" value={d.lot_no || ""} onChange={(e) => set(i, { lot_no: e.target.value })}>
              <option value="">Batch…</option>
              {(onHand.lots || []).map((l) => (
                <option key={l.id} value={l.lot_no}>
                  {l.lot_no} · {fmtQty(l.qty)}
                  {l.expiry_date ? ` · exp ${String(l.expiry_date).slice(0, 10)}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <input className="form-control form-control-sm" style={{ ...inputStyle, width: 140 }} placeholder="Batch no." aria-label="Batch number" value={d.lot_no || ""} onChange={(e) => set(i, { lot_no: e.target.value })} />
          )}
          <input type="number" step="any" min={0} className="form-control form-control-sm" style={{ ...inputStyle, width: 90 }} placeholder="Qty" aria-label="Batch quantity" value={d.qty ?? ""} onChange={(e) => set(i, { qty: e.target.value })} />
          {mode === "in" ? (
            <>
              <input type="date" className="form-control form-control-sm" style={{ ...inputStyle, width: 150 }} aria-label="Expiry date" title="Expiry date" value={d.expiry_date || ""} onChange={(e) => set(i, { expiry_date: e.target.value })} />
              <input className="form-control form-control-sm" style={{ ...inputStyle, width: 110 }} placeholder="DOT (WWYY)" aria-label="DOT week and year" value={d.dot || ""} onChange={(e) => set(i, { dot: e.target.value })} />
            </>
          ) : null}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onChange(list.filter((_, k) => k !== i))}>
            Remove
          </button>
        </div>
      ))}
      <div className="d-flex align-items-center gap-2">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => onChange([...list, { lot_no: "", qty: list.length ? "" : qty }])}>
          Add batch
        </button>
        <span style={{ fontSize: 12, color: Math.abs(total - Number(qty || 0)) < 1e-9 || (mode === "out" && !list.length) ? "#6B7280" : "#B91C1C" }}>
          {mode === "out" && !list.length ? "Earliest expiry goes first" : `${fmtQty(total)} of ${fmtQty(qty)} in batches`}
        </span>
      </div>
    </div>
  );
}

// Batch / serial details ready for the API (numbers as numbers; empty rows dropped).
export const cleanDetails = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((d) => d.serial_no || d.lot_no)
    .map((d) => (d.serial_no ? { serial_no: d.serial_no } : { lot_no: d.lot_no, qty: Number(d.qty || 0), expiry_date: d.expiry_date || undefined, dot: d.dot || undefined }));
